import { type FC, useCallback, useEffect } from 'react';
import BaseLayout from '@/base/Layout';
import {
  UiHStack,
  UiStack,
  UiButton,
  uiStyles,
  UiText,
  UiLink,
  UiIconArrowSquareOut
} from '@/lib/ui';
import SmartWindow from '@/base/Layout/SmartWindow';
import BaseAppRegistrationCard from '@/base/App/RegistrationCard';
import ManageEventLayoutBody from '@/registration/component/BuildEvent/LayoutBody';
import ManageEventSteps from '@/registration/component/BuildEvent/Steps';
import BaseAppListDrawerButton from '@/base/App/ListDrawerButton';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { InfoFormType } from '@/api/registration';
import { useIsSetMutation } from '@/registration/hook/useIsSetMutation';
import { useBuildEventSteps } from '@/registration/hook/useBuildEventSteps';
import StepInstruction from '@/registration/component/BuildEvent/StepInstruction';
import FormList from '@/registration/buildEvent/AdditionalInfoForm/FormList';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import { PermissionAction, PermissionDomain } from '@/api/constant/adminUserPermission';
import BaseAclContainer from '@/account/component/AclContainer';

const AdditionalInfoForm: FC = () => {
  const { eventId, tenant } = useRegisterRoute();
  const { toNextStep } = useBuildEventSteps();

  const { mutate, isLoading } = useIsSetMutation();

  // Assume the step is set once the step is visited.
  useEffect(
    () => { return mutate({ eventId, key: 'additionalInformation', bool: true }); },
    [mutate, eventId]
  );

  return (
    <>
      <BaseLayout
        smartWindow={(<SmartWindow />)}
        // disableTopNav={true}
        appSwitch={(<BaseAppListDrawerButton currentApp={<BaseAppRegistrationCard showDescription={false} />} />)}
        MainTransition={null}
      >
        <ManageEventLayoutBody
          steps={(
            <ManageEventSteps
              eventId={eventId}
              activeStep={'additionalInfo'}
            />
          )}
          message={'Group your input fields.'}
        >
          <UiStack
            // pt={4}
            spacing={4}
            flexGrow={1}
            p={8}
          >
            <UiHStack justifyContent={'flex-start'}>
              <StepInstruction label={'Instructions'}>
                <UiStack p={8} py={6} borderRadius={uiStyles.borderRadius} spacing={4} enableDivider={true}>
                  <UiText>
                    You can create different additional information forms for different attendee groups.
                  </UiText>
                  <UiLink href="https://support.evexus.com/agent/evexus/evexus/knowledge-base/page?articlestatus=published&showAllArticles=true&rootcategoryId=19910000000386449&categoryId=19910000000386449&status=published#Solutions/dv/19910000000288929/en" target={'_blank'}>
                    <UiHStack
                      {...uiStyles.hover}
                      borderRadius={uiStyles.borderRadius}
                      p={2}
                    >
                      <UiText
                        color={'primary.500'}
                      >
                        Learn more about Additional information
                      </UiText>
                      <UiIconArrowSquareOut color={'primary.500'} />
                    </UiHStack>
                  </UiLink>
                </UiStack>
              </StepInstruction>
            </UiHStack>
            <BaseDividerHorizontal height={4} />
            <FormList formType={InfoFormType.ADDITIONAL} />
          </UiStack>
          <UiHStack p={8} justifyContent={'space-between'}>
            <UiStack flexGrow={1} />
            <BaseAclContainer tenantId={tenant?.id ?? 0} permissionsRequired={[[PermissionDomain.Registration, PermissionAction.Write]]}>
              <UiButton
                size={'lg'}
                colorScheme={'primary'}
                type={'submit'}
                fontSize={'lg'}
                onClick={toNextStep}
                isLoading={isLoading}
              >
                Next
              </UiButton>
            </BaseAclContainer>
          </UiHStack>
        </ManageEventLayoutBody>
      </BaseLayout>
    </>
  );
};

export default AdditionalInfoForm;
